import React, { useEffect, useState } from "react";
import { Loader2, Shield, ShieldCheck, Copy, Check, AlertTriangle } from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Admin MFA setup / manage dialog.
 *
 * Two states depending on `mfaEnabled`:
 *
 *   OFF → shows QR code + provisional secret + confirmation-code input.
 *         POST /admin/mfa/setup-start returns the QR PNG data-URI so we
 *         render it inline with no extra network round-trip.
 *         POST /admin/mfa/setup-confirm returns the 8 one-time recovery
 *         codes exactly once — we render them prominently with copy-all.
 *
 *   ON  → shows the "enabled since <date>" state and instructs the admin
 *         to ask a peer platform_admin to disable it if their phone is
 *         lost (self-disable is blocked server-side by design).
 *
 * On close we call `onDone` so the parent can refetch /admin/me and
 * flip the "MFA is on" banner in the sidebar.
 */
export default function AdminMfaSetupDialog({ open, onOpenChange, me, onDone }) {
    // Setup state
    const [starting, setStarting] = useState(false);
    const [setup, setSetup] = useState(null);   // {secret, otpauth_uri, qr_png_data_url}
    const [code, setCode] = useState("");
    const [confirming, setConfirming] = useState(false);
    const [recoveryCodes, setRecoveryCodes] = useState(null);
    const [copiedSecret, setCopiedSecret] = useState(false);
    const [copiedCodes, setCopiedCodes] = useState(false);

    const mfaEnabled = !!me?.mfa_enabled;

    // Kick the setup flow the first time the dialog opens for an OFF admin.
    useEffect(() => {
        if (!open || mfaEnabled) return;
        if (setup || recoveryCodes) return;   // already started this session
        (async () => {
            try {
                setStarting(true);
                const { data } = await api.post("/admin/mfa/setup-start", {});
                setSetup(data);
            } catch (err) {
                toast.error(formatApiError(err));
                onOpenChange(false);
            } finally {
                setStarting(false);
            }
        })();
    }, [open, mfaEnabled, setup, recoveryCodes, onOpenChange]);

    const reset = () => {
        setSetup(null); setCode(""); setRecoveryCodes(null);
        setCopiedSecret(false); setCopiedCodes(false);
    };

    const confirm = async (e) => {
        e?.preventDefault();
        try {
            setConfirming(true);
            const { data } = await api.post("/admin/mfa/setup-confirm", { code });
            setRecoveryCodes(data.recovery_codes);
            toast.success("MFA enabled. Save the recovery codes below.");
            // Don't auto-close — the recovery codes are shown only once.
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setConfirming(false);
        }
    };

    const finish = () => {
        reset();
        onOpenChange(false);
        onDone?.();
    };

    const copySecret = async () => {
        try { await navigator.clipboard.writeText(setup.secret); setCopiedSecret(true); setTimeout(() => setCopiedSecret(false), 1200); }
        catch { /* silent */ }
    };
    const copyRecoveryCodes = async () => {
        try {
            await navigator.clipboard.writeText(recoveryCodes.join("\n"));
            setCopiedCodes(true); setTimeout(() => setCopiedCodes(false), 1500);
            toast.success("Copied 8 recovery codes to clipboard");
        } catch { toast.error("Copy failed — select the codes manually."); }
    };

    // ------------------------------ ENABLED state ------------------------------
    if (mfaEnabled) {
        return (
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent data-testid="admin-mfa-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <ShieldCheck size={16} className="text-emerald-500" />
                            MFA is enabled
                        </DialogTitle>
                        <DialogDescription>
                            Two-factor authentication is protecting <b>{me?.email}</b>.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="text-xs text-muted-foreground rounded-lg border border-border bg-muted/30 p-3">
                        <p className="mb-2">
                            <b className="text-foreground">Lost your device?</b> Self-disable is blocked
                            for security. Ask another platform_admin to disable your MFA using
                            <code className="ml-1">POST /api/admin/mfa/disable</code> — they'll need to
                            supply a reason for the audit log.
                        </p>
                        <p>
                            <b className="text-foreground">Rotate the secret?</b> Disable + re-enroll
                            is currently the only path. Coming soon: in-place rotate.
                        </p>
                    </div>
                    <DialogFooter>
                        <button
                            type="button"
                            onClick={() => onOpenChange(false)}
                            className="btn-outline text-sm px-4 py-2"
                            data-testid="admin-mfa-close"
                        >
                            Close
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }

    // ------------------------------ SETUP state --------------------------------
    return (
        <Dialog
            open={open}
            onOpenChange={(v) => {
                // Once recovery codes have been minted, force the admin through
                // the "I saved them" confirmation — no accidental Escape-close
                // that loses the codes forever.
                if (recoveryCodes && open && !v) return;
                if (!v) reset();
                onOpenChange(v);
            }}
        >
            <DialogContent
                data-testid="admin-mfa-dialog"
                onEscapeKeyDown={(e) => { if (recoveryCodes) e.preventDefault(); }}
                onPointerDownOutside={(e) => { if (recoveryCodes) e.preventDefault(); }}
                onInteractOutside={(e) => { if (recoveryCodes) e.preventDefault(); }}
                className={`max-w-lg ${recoveryCodes ? "[&>button.absolute]:hidden" : ""}`}
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Shield size={16} className="text-violet-500" />
                        {recoveryCodes ? "Save your recovery codes" : "Enable Two-Factor Authentication"}
                    </DialogTitle>
                    <DialogDescription>
                        {recoveryCodes
                            ? "Store these somewhere safe (1Password, Bitwarden, offline notebook). Each code works exactly once — you'll never see them again."
                            : "Scan the QR code with any TOTP app (1Password, Authy, Google Authenticator) and enter the 6-digit code to confirm."}
                    </DialogDescription>
                </DialogHeader>

                {starting ? (
                    <div className="py-10 flex justify-center">
                        <Loader2 size={20} className="animate-spin text-violet-500" />
                    </div>
                ) : recoveryCodes ? (
                    // ----- Recovery codes reveal -------------------------------
                    <>
                        <div className="rounded-lg border border-amber-400/40 bg-amber-500/10 p-3 text-xs text-amber-800 dark:text-amber-300 flex gap-2">
                            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                            <span>
                                These codes are the ONLY way back in if your authenticator device is lost.
                                They&apos;re not stored in plaintext on our side — closing this dialog
                                without saving them means asking a peer platform_admin to disable MFA
                                and re-enroll.
                            </span>
                        </div>
                        <div
                            className="mt-3 grid grid-cols-2 gap-2 font-mono text-sm bg-muted/30 border border-border rounded-lg p-3"
                            data-testid="admin-mfa-recovery-codes"
                        >
                            {recoveryCodes.map((c) => (
                                <div key={c} className="tracking-wider text-foreground">{c}</div>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={copyRecoveryCodes}
                            className="mt-3 text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded border border-border hover:bg-muted/50"
                            data-testid="admin-mfa-copy-codes"
                        >
                            {copiedCodes ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy all 8 codes</>}
                        </button>
                        <DialogFooter>
                            <button
                                type="button"
                                onClick={finish}
                                className="btn-green text-sm px-4 py-2"
                                data-testid="admin-mfa-done"
                            >
                                I&apos;ve saved them — done
                            </button>
                        </DialogFooter>
                    </>
                ) : setup ? (
                    // ----- QR + confirmation -----------------------------------
                    <form onSubmit={confirm} className="space-y-3">
                        <div className="flex flex-col sm:flex-row gap-4">
                            <img
                                src={setup.qr_png_data_url}
                                alt="MFA QR code"
                                width={180}
                                height={180}
                                data-testid="admin-mfa-qr"
                                className="rounded-lg border border-border bg-white p-2 self-start"
                            />
                            <div className="flex-1 space-y-2">
                                <div className="text-xs text-muted-foreground">Can&apos;t scan? Enter this key manually:</div>
                                <div className="flex items-center gap-2 font-mono text-xs bg-muted/40 border border-border rounded p-2">
                                    <span className="flex-1 break-all text-foreground" data-testid="admin-mfa-secret">
                                        {setup.secret}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={copySecret}
                                        className="shrink-0 p-1 rounded hover:bg-background text-muted-foreground hover:text-foreground"
                                        data-testid="admin-mfa-copy-secret"
                                        aria-label="Copy secret"
                                    >
                                        {copiedSecret ? <Check size={12} /> : <Copy size={12} />}
                                    </button>
                                </div>
                                <div className="text-[11px] text-muted-foreground">
                                    Account name will show as your admin email. Issuer will show as
                                    &quot;Simonara&quot;.
                                </div>
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="mfa-code">6-digit code from your app</Label>
                            <Input
                                id="mfa-code"
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]{6}"
                                maxLength={6}
                                autoFocus
                                required
                                value={code}
                                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                                data-testid="admin-mfa-code"
                                className="mt-1 font-mono text-lg tracking-widest text-center"
                                placeholder="000000"
                            />
                        </div>
                        <DialogFooter>
                            <button
                                type="button"
                                onClick={() => onOpenChange(false)}
                                className="btn-outline text-sm px-4 py-2"
                                data-testid="admin-mfa-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={confirming || code.length !== 6}
                                className="btn-green text-sm px-4 py-2 disabled:opacity-60"
                                data-testid="admin-mfa-confirm"
                            >
                                {confirming
                                    ? <Loader2 size={13} className="animate-spin" />
                                    : "Verify & enable MFA"}
                            </button>
                        </DialogFooter>
                    </form>
                ) : null}
            </DialogContent>
        </Dialog>
    );
}
